import { toLandingPagePlansData } from '@provision/landing-page-ui';
import {
  DEFAULT_LANDING_PAGE_CONFIG,
  type LandingPageConfig,
  parseLandingPageConfig,
} from '@provision/landing-page-utils';
import type { MembershipPlansPreviewConfig } from '@provision/membership-plans-ui';
import { useMemo } from 'react';
import { type LandingPageFormValues } from 'src/pages/Settings/memberships/components/landing-page-tab/types';
import { serializeLandingPageConfig } from 'src/pages/Settings/memberships/utils/serialize-landing-page-config';
import useDebouncedValue from 'src/utils/hooks/use-debounced-value';

const PREVIEW_DEBOUNCE_MS = 200;

interface UseLandingPagePreviewParams {
  data: LandingPageFormValues;
  liveFormValues: LandingPageFormValues | null;
  storedConfig: string | null | undefined;
  plansConfig: MembershipPlansPreviewConfig | null;
}

export const useLandingPagePreview = ({
  data,
  liveFormValues,
  storedConfig,
  plansConfig,
}: UseLandingPagePreviewParams) => {
  const values = useDebouncedValue(liveFormValues ?? data, PREVIEW_DEBOUNCE_MS);

  // Keyed on `values`, never its branches: react-hook-form emits a shallow spread, so
  // `blocks` and `settings` keep their identity and a field edit would not invalidate this.
  const config = useMemo((): LandingPageConfig => {
    const serialized = serializeLandingPageConfig({
      storedConfig,
      blocks: values.blocks,
      settings: values.settings,
    });

    return parseLandingPageConfig(serialized) ?? DEFAULT_LANDING_PAGE_CONFIG;
  }, [storedConfig, values]);

  const plans = useMemo(() => toLandingPagePlansData(plansConfig), [plansConfig]);

  return { config, plans };
};
